import {
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
} from '@chakra-ui/react';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { StrictFieldValues, StringPath } from './types';
import { Select } from 'packages/common-frontend/components/Select';

export type SelectInputProps<T extends StrictFieldValues> = {
  name: StringPath<T>;
  label?: string;
  // options can hold arbitrary properties, but label and value must be present
  options: {
    label: React.ReactNode;
    value: string;
    properties?: { [key: string]: any };
  }[];
  placeholder?: string;
  helperText?: React.ReactNode;
  required?: boolean;
  isDisabled?: boolean;
  tag?: React.ReactNode;
};

/**
 * A dropdown select input component for form selection fields.
 *
 * @template T - The form data type that extends StrictFieldValues
 *
 * Props:
 * - `name`: Field name in the form. Must be a string field
 * - `label`: Optional label displayed above the select
 * - `options`: Array of options to display in the dropdown. Each option must have label and value, and can include additional properties
 * - `placeholder`: Optional placeholder text shown when no option is selected
 * - `helperText`: Optional helper text displayed below the select
 * - `required`: When true, field is required and shows validation error if empty
 * - `isDisabled`: When true, the select is disabled and cannot be interacted with
 * - `tag`: Optional, can pass a react component to render as a tag next to the input
 *
 * @example
 * // Basic select
 * <SelectInput<FormData>
 *   name="country"
 *   label="Country"
 *   options={[
 *     { label: 'United States', value: 'us' },
 *     { label: 'Canada', value: 'ca' }
 *   ]}
 * />
 *
 * @example
 * // Required select with placeholder
 * <SelectInput<FormData>
 *   name="department"
 *   label="Department"
 *   placeholder="Choose a department"
 *   required
 *   options={[
 *     { label: 'Engineering', value: 'eng' },
 *     { label: 'Sales', value: 'sales' }
 *   ]}
 * />
 */
export const SelectInput = <T extends StrictFieldValues>({
  name,
  label,
  options,
  placeholder = '',
  helperText = '',
  required,
  isDisabled = false,
  tag,
}: SelectInputProps<T>) => {
  const { control } = useFormContext<T>();
  const rules = required
    ? {
        required: { value: true, message: 'This is a required field' },
      }
    : undefined;

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState: { error } }) => {
        return (
          <FormControl isInvalid={!!error}>
            <Flex alignItems="center">
              {label && <FormLabel fontWeight="bold">{label}</FormLabel>}
              {tag}
            </Flex>
            <Select
              isDisabled={isDisabled}
              placeholderLabel={placeholder}
              isInvalid={!!error}
              // Can cast to string here because StringPath ensures that this field's
              // value is a string
              value={field.value as string}
              onChange={(nextValue) => {
                field.onChange(nextValue);
              }}
              options={options}
              onClose={field.onBlur}
              showEmpty
            />
            <FormErrorMessage>{error?.message}</FormErrorMessage>
            <FormHelperText>{helperText}</FormHelperText>
          </FormControl>
        );
      }}
    />
  );
};
